import { useTranslator } from '@/hooks/useTranslator'
import { Box, Typography } from '@mui/material'

interface AttendanceData {
  id: number
  month: string
  user: {
    first_name: string
    last_name?: string
    employee_id: string
  }
  attendance: Array<{
    date: number
    status: 'absent' | 'working_day' | 'non_working_day'
    day_of_month: string
  }>
  attendance_status: {
    absent: number
    non_working_day: number
    working_day: number
  }
  working_day: number
  day_start_of_month: number
}

interface AttendanceEmployeeTableProps {
  data?: AttendanceData
  minimum_working_days?: any
  withdraw_cutoff_day?: number
}

export const AttendanceEmployeeTable = ({
  data,
  minimum_working_days,
  withdraw_cutoff_day
}: AttendanceEmployeeTableProps) => {
  const { __ } = useTranslator()

  if (!data || !data.attendance) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <div className='h-[200px] flex items-center justify-center border border-[#e0e0e0] rounded-md'>
          <Typography className='text-[15px] font-normal text-[#2f2b3de6]'>{__('No attendance data')}</Typography>
        </div>
      </Box>
    )
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'working_day':
        return {
          color: '#77a0ff'
        }
      case 'absent':
        return {
          color: '#ff9497'
        }
      case 'non_working_day':
        return {
          color: '#c0bec5'
        }
      default:
        return {
          color: '#E0E0E0'
        }
    }
  }

  // Use actual attendance data length instead of calendar month days
  const daysInMonth = data.attendance.length

  // day_start_of_month: 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat, 0=Sun
  const startDay = data.day_start_of_month === 0 ? 6 : data.day_start_of_month - 1

  const weeks = []
  const totalCells = Math.ceil((daysInMonth + startDay) / 7) * 7

  for (let i = 0; i < totalCells; i += 7) {
    const week = []

    for (let j = 0; j < 7; j++) {
      const cellIndex = i + j
      const dayNumber = cellIndex - startDay + 1

      if (cellIndex < startDay || dayNumber > daysInMonth) {
        week.push(null)
      } else {
        week.push(dayNumber)
      }
    }

    weeks.push(week)
  }

  const getAttendanceForDay = (day: number) => {
    return data.attendance.find(att => att.date === day)
  }

  const formatDayOfMonth = (dayOfMonth: string) => {
    const date = new Date(dayOfMonth)
    const day = date.getDate()

    return `${day}`
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #e0e0e0'
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#eaeaec' }}>
              {[__('Mon'), __('Tue'), __('Wed'), __('Thu'), __('Fri'), __('Sat'), __('Sun')].map((day, index) => (
                <th
                  key={index}
                  style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    borderRight: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: '300',
                    fontSize: '13px',
                    minWidth: '80px'
                  }}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIndex) => (
              <tr key={weekIndex}>
                {week.map((day, dayIndex) => {
                  const attendance = day ? getAttendanceForDay(day) : null
                  const statusConfig = attendance ? getStatusConfig(attendance.status) : null

                  // Determine background color based on conditions
                  const getBackgroundColor = () => {
                    if (day === null) return '#f9f9f9'
                    if (minimum_working_days && day <= minimum_working_days) return '#2e263d0a'
                    const currentDayOfMonth = attendance?.day_of_month ? new Date(attendance.day_of_month).getDate() : day
                    if (withdraw_cutoff_day && withdraw_cutoff_day > 0 && currentDayOfMonth > withdraw_cutoff_day)
                      return '#2e263d0a'

                    return 'transparent'
                  }

                  return (
                    <td
                      key={dayIndex}
                      style={{
                        padding: '8px',
                        textAlign: 'center',
                        borderRight: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0',
                        minHeight: '60px',
                        verticalAlign: 'middle',
                        backgroundColor: getBackgroundColor()
                      }}
                    >
                      {day && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: '500',
                              fontSize: '13px',
                              color: '#2f2b3de6'
                            }}
                          >
                            {attendance?.day_of_month ? formatDayOfMonth(attendance.day_of_month) : day}
                          </Typography>
                          {statusConfig && (
                            <div
                              className='w-[16px] h-[16px] rounded-full'
                              style={{ backgroundColor: statusConfig.color }}
                            ></div>
                          )}
                        </Box>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  )
}
